import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SKIP_BUSY_INDICATOR, SKIP_ERROR_TOAST } from 'src/app/shared/http-context-tokens';
import { DownloadedFile, fileNameOf } from '../safety-rounds/safety-round.service';
import {
  AssessExposureFactorRequest,
  AssignedExposureTemplate,
  CompleteExposureAssessmentRequest,
  ExposureAssessmentDetail,
  ExposureAssessmentSummary,
  ExposurePermissions,
  ExposureProfile,
  SaveExposureDepartmentRequest,
  SaveExposureProfileRequest,
  StartExposureAssessmentRequest,
  StartExposureAssessmentResponse,
  UpdateExposureAssessmentDetailsRequest,
} from './exposure-register.models';

/**
 * Exposure Register for one business — FlexiExposureAssessmentsController.
 *
 * Reads are open to anyone who may open the business; every write is refused
 * by the backend to anyone but an Admin, one of the business's HSE advisors or
 * its OHS responsible. Like the safety rounds, the route lives on the v1
 * prefix (`api/kuba/v1/flexi/...`) beside the rest of Flexi, so it is composed
 * from BASE_URL rather than BASE_URL_V2.
 *
 * Errors are left to the global interceptor toast unless a method says
 * otherwise. The autosave writes (details, departments, factors) skip the
 * global busy indicator and the toast: the wizard shows its own save state and
 * re-reads the assessment when one fails.
 */
@Injectable({ providedIn: 'root' })
export class FlexiExposureRegisterService {
  private readonly base = `${environment.BASE_URL}/flexi/exposure-assessments`;

  constructor(private http: HttpClient) {}

  /**
   * Whether the caller may start, edit and complete assessments and edit the
   * basis texts. A failed probe leaves the page read-only rather than broken,
   * so it is answered quietly.
   */
  permissions(businessId: number): Observable<ExposurePermissions> {
    return this.http
      .get<ExposurePermissions | null>(`${this.base}/${businessId}/permissions`, {
        context: new HttpContext().set(SKIP_ERROR_TOAST, true),
      })
      .pipe(
        map(result => ({ CanAuthor: result?.CanAuthor === true })),
        catchError(() => of({ CanAuthor: false })),
      );
  }

  list(businessId: number): Observable<ExposureAssessmentSummary[]> {
    return this.http
      .get<ExposureAssessmentSummary[] | null>(`${this.base}/${businessId}`)
      .pipe(map(rows => rows ?? []));
  }

  profile(businessId: number): Observable<ExposureProfile> {
    return this.http.get<ExposureProfile>(`${this.base}/${businessId}/profile`);
  }

  saveProfile(businessId: number, request: SaveExposureProfileRequest): Observable<void> {
    return this.http.put<void>(`${this.base}/${businessId}/profile`, request);
  }

  /**
   * Published templates assigned to the business, its default first. The
   * first call for a business with no assignments also assigns the
   * application's defaults.
   */
  templates(businessId: number): Observable<AssignedExposureTemplate[]> {
    return this.http
      .get<AssignedExposureTemplate[] | null>(`${this.base}/${businessId}/templates`)
      .pipe(map(rows => rows ?? []));
  }

  /** Starts an assessment and returns its id. 409 while another is in progress. */
  start(businessId: number, request: StartExposureAssessmentRequest): Observable<number> {
    return this.http
      .post<StartExposureAssessmentResponse | number>(`${this.base}/${businessId}`, request)
      .pipe(map(result => Number(typeof result === 'number' ? result : result?.Id)));
  }

  get(businessId: number, assessmentId: number): Observable<ExposureAssessmentDetail> {
    return this.http.get<ExposureAssessmentDetail>(`${this.base}/${businessId}/${assessmentId}`);
  }

  /** Title, responsible and the step to resume at. Handled locally by the caller. */
  updateDetails(
    businessId: number,
    assessmentId: number,
    request: UpdateExposureAssessmentDetailsRequest,
  ): Observable<void> {
    return this.http.put<void>(`${this.base}/${businessId}/${assessmentId}`, request, {
      context: autosaveContext(),
    });
  }

  /** Autosave of step 2. Excluding a department also removes it from every factor. */
  saveDepartments(
    businessId: number,
    assessmentId: number,
    request: SaveExposureDepartmentRequest[],
  ): Observable<void> {
    return this.http.put<void>(`${this.base}/${businessId}/${assessmentId}/departments`, request, {
      context: autosaveContext(),
    });
  }

  /** Autosave of one factor in step 3. */
  assessFactor(
    businessId: number,
    assessmentId: number,
    factorId: number,
    request: AssessExposureFactorRequest,
  ): Observable<void> {
    return this.http.put<void>(`${this.base}/${businessId}/${assessmentId}/factors/${factorId}`, request, {
      context: autosaveContext(),
    });
  }

  /** 400 while a factor is unassessed or no department is included; 409 when already completed. */
  complete(businessId: number, assessmentId: number, request: CompleteExposureAssessmentRequest): Observable<void> {
    return this.http.post<void>(`${this.base}/${businessId}/${assessmentId}/complete`, request);
  }

  /** Soft delete; 409 once completed. */
  delete(businessId: number, assessmentId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${businessId}/${assessmentId}`);
  }

  /** The archived report of a completed assessment, or a preview of one in progress. */
  downloadReport(businessId: number, assessmentId: number): Observable<DownloadedFile> {
    return this.http
      .get(`${this.base}/${businessId}/${assessmentId}/report`, { observe: 'response', responseType: 'blob' })
      .pipe(map(response => ({ blob: response.body ?? new Blob(), fileName: fileNameOf(response) })));
  }
}

function autosaveContext(): HttpContext {
  return new HttpContext().set(SKIP_ERROR_TOAST, true).set(SKIP_BUSY_INDICATOR, true);
}
